import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { extractError } from "../lib/errors";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ArrowLeft, ClipboardCheck } from "lucide-react";
import { FRAMEWORKS as ALL_FRAMEWORKS } from "../constants/frameworks";

import AuditPlanTab from "./audit_detail/AuditPlanTab";
import AuditChecklistTab from "./audit_detail/AuditChecklistTab";
import AuditFindingsTab from "./audit_detail/AuditFindingsTab";
import AuditReportTab from "./audit_detail/AuditReportTab";
import AuditFollowupTab from "./audit_detail/AuditFollowupTab";

const STATUS_COLORS = {
  planned: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  in_progress: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  completed: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  cancelled: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

const PHASE_COLORS = {
  initiation: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
  preparation: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400",
  execution: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  reporting: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  followup: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400",
  closed: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
};

/**
 * AuditDetail — Full-page route /audits/:auditId (ISO 19011 §6 flow).
 *
 * ADR-S16a-003: replaces the Dialog modal for audit detail.
 * Tabs: Plan | Checklist | Findings | Report | Follow-up.
 * No double-clamp: AppLayout provides the outer chrome, each tab manages its own scroll.
 */
const AuditDetail = () => {
  const { auditId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { t } = useI18n();

  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const frameworks = ALL_FRAMEWORKS.map((f) => ({ value: f.id, label: f.short }));

  const fetchAudit = async () => {
    try {
      const res = await axios.get(`${API}/audits/${auditId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAudit(res.data);
    } catch (e) {
      toast.error(extractError(e, "Failed to load audit"));
      navigate("/audits");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (auditId) fetchAudit();
  }, [auditId]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-64 bg-muted rounded" />
          <div className="h-4 w-48 bg-muted rounded" />
        </div>
      </div>
    );
  }

  if (!audit) return null;

  const phase = audit.phase || "initiation";
  const statusColor = STATUS_COLORS[audit.status] || "";
  const phaseColor = PHASE_COLORS[phase] || "";

  return (
    <div className="p-6 space-y-6 flex flex-col min-h-[calc(100vh-4rem)]">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <button
          onClick={() => navigate("/audits")}
          className="flex items-center gap-1 hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("audits.back_to_audits")}
        </button>
        <span>/</span>
        <span className="text-foreground font-medium truncate">{audit.title}</span>
      </div>

      <PageHeader
        title={audit.title}
        icon={ClipboardCheck}
        description={
          audit.lead_auditor
            ? `${t("audits.lead_inline").replace("{name}", audit.lead_auditor)}`
            : undefined
        }
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className={statusColor}>
              {t(`audits.status.${audit.status}`) || audit.status}
            </Badge>
            <Badge variant="outline" className={phaseColor}>
              {t(`audits.phase.${phase}`) || phase}
            </Badge>
            {audit.locale && (
              <Badge variant="secondary" className="text-xs">
                {audit.locale.toUpperCase()}
              </Badge>
            )}
            {(audit.frameworks || []).map((fw) => (
              <Badge key={fw} variant="secondary" className="text-xs">
                {frameworks.find((f) => f.value === fw)?.label || fw}
              </Badge>
            ))}
          </div>
        }
      />

      {/* Tabs — full-page, each tab manages its own scroll */}
      <Tabs defaultValue="plan" className="flex-1 flex flex-col">
        <TabsList className="w-full sm:w-auto self-start">
          <TabsTrigger value="plan">{t("audits.tab.plan")}</TabsTrigger>
          <TabsTrigger value="checklist">
            {t("audits.tab.checklist")}
            {audit.checklist?.length > 0 && (
              <span className="ml-1.5 text-xs text-muted-foreground">({audit.checklist.length})</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="findings">{t("audits.tab.findings")}</TabsTrigger>
          <TabsTrigger value="report">{t("audits.tab.report")}</TabsTrigger>
          <TabsTrigger value="followup">{t("audits.tab.followup")}</TabsTrigger>
        </TabsList>

        <TabsContent value="plan" className="mt-4 flex-1">
          <AuditPlanTab audit={audit} onRefresh={fetchAudit} />
        </TabsContent>

        <TabsContent value="checklist" className="mt-4 flex-1 flex flex-col min-h-0">
          <AuditChecklistTab audit={audit} onRefresh={fetchAudit} />
        </TabsContent>

        <TabsContent value="findings" className="mt-4 flex-1 flex flex-col min-h-0">
          <AuditFindingsTab audit={audit} />
        </TabsContent>

        <TabsContent value="report" className="mt-4 flex-1">
          <AuditReportTab audit={audit} />
        </TabsContent>

        <TabsContent value="followup" className="mt-4 flex-1">
          <AuditFollowupTab audit={audit} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AuditDetail;
